<template>
  <b-container>
    <b-row v-if="state === 'sites'">
      <b-col>
        <b-button class="btn-add-site" variant="primary" @click="addSite">Add Site</b-button>
        <b-table hover
          :items="displaySites"
          :fields="tableFields"
          @row-clicked="selectSite"
          class="sites"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc" />
      </b-col>
    </b-row>

    <div v-if="state === 'site'">
      <!-- <b-row class="row section-title">
        <h3 class="site-name">{{ sites[selectedSiteId].jobNumber }}</h3>
      </b-row> -->
      <b-row class="row section-title">
        <h3 class="site-name">{{ sites[selectedSiteId].name }}</h3>
      </b-row>
      <b-row class="section-alert-dark next-btn-container justify-content-between">
        <b-button class="btn w-50 ml-n1" variant="secondary" @click="backToSites">
          <font-awesome-icon icon="arrow-circle-left" size="lg" class="icon" /> Back
        </b-button>
        <b-button class="btn w-50 mr-n1" variant="secondary" @click="editSite">Edit</b-button>
      </b-row>
      <b-row class="justify-content-between">
        <vue-confirmation-button v-if="showDeactivate" ref="deleteButton" :css="'btn btn-danger btn-confirmation'" :messages="['Deactivate','Are you sure?','Deactivating.....']" @confirmation-success="deactivate(sites[selectedSiteId].id);"/>
        <vue-confirmation-button v-if="showActivate" ref="activateButton" :css="'btn btn-danger btn-confirmation'" :messages="['Activate','Are you sure?','Activating.....']" @confirmation-success="activate(sites[selectedSiteId].id);"/>
      </b-row>
      <Site :boundSite="sites[selectedSiteId]" :editing="false" :loadSignIns="true" :showQRCode="true" />
    </div>

    <b-modal ref="siteModal" size="lg" hide-footer no-close-on-backdrop :title="(sites[selectedSiteId] || {name: 'Add New Site'}).name">
      <SiteEdit @cancel="cancelEditing" @siteUpdated="saved" :boundSite="sites[selectedSiteId]" :editing="true" :site="sites[selectedSiteId]"/>
    </b-modal>
  </b-container>
</template>

<style lang="scss">
  @import "../assets/main.scss";
</style>

<script>
import {
  getMyUser, getSites, deleteSite, activateSite,
} from '@/lib/api';
import { EventBus } from '@/lib/event-bus';
import Site from '@/components/Site.vue';
import SiteEdit from '@/components/SiteEdit.vue';
import emailAddressTranslation from '@/lib/email-address-translation';
import vueConfirmationButton from 'vue-confirmation-button';

export default {
  name: 'Sites',
  components: {
    Site,
    SiteEdit,
    vueConfirmationButton,
  },
  data() {
    return {
      user: null,
      state: 'sites',
      editing: false,
      sites: [],
      pageTitle: 'Sites',
      selectedSiteId: null,
      sortBy: 'name',
      sortDesc: false,
      addingNewSite: false,
      tableFields: [{
        key: 'jobNumber',
        label: 'Job Number',
        sortable: true,
      },
      {
        key: 'name',
        label: 'Site Name',
        sortable: true,
      },
      {
        key: 'ownerName',
        label: 'Contract Manager',
        sortable: true,
      },
      {
        key: 'forepersonName',
        label: 'Site Contact',
        sortable: true,
      },
      {
        key: 'activeDisplay',
        label: 'Active?',
        sortable: true,
      }],
    };
  },
  computed: {
    displaySites() {
      return this.sites.map((site) => this.translateSite(site));
    },
    showDeactivate() {
      return this.user && this.user.groups && this.user.groups.indexOf('Administrators') !== -1 && this.sites[this.selectedSiteId] && this.sites[this.selectedSiteId].active;
    },
    showActivate() {
      return this.user && this.user.groups && this.user.groups.indexOf('Administrators') !== -1 && this.sites[this.selectedSiteId] && !this.sites[this.selectedSiteId].active;
    },
  },
  mounted() {
    // get current user
    const self = this;
    EventBus.$emit('showLoader');
    EventBus.$emit('isCompact', true);
    getMyUser().then((user) => {
      self.user = user;
      self.loadSites();
    });
  },
  methods: {
    async loadSites() {
      EventBus.$emit('showLoader');
      const sites = await getSites();
      this.sites = sites
        .filter((site) => (this.user && this.user.groups && (this.user.groups.indexOf('Administrators') >= 0
          || site.ownerUsername === this.user.email
          || site.forepersonUsername === this.user.email)));
      EventBus.$emit('hideLoader');
    },
    addSite() {
      this.selectedSiteId = this.sites.length;
      this.$refs.siteModal.show();
    },
    selectSite(site) {
      const idx = this.sites.indexOf(site);
      this.selectedSiteId = idx;
      this.state = 'site';
    },
    editSite() {
      this.$refs.siteModal.show();
    },
    translateSite(site) {
      /* eslint-disable no-param-reassign */
      site.activeDisplay = site.active ? 'Yes' : 'No';
      /** We have made an assumption here that the owner is
       *  going to be from shick.
       *  With format first.last@shick.co.nz
       */
      site.ownerName = emailAddressTranslation(site.ownerUsername);
      site.forepersonName = emailAddressTranslation(site.forepersonUsername);
      /* eslint-enable no-param-reassign */
      return site;
    },
    async deactivate(siteId) {
      this.$refs.siteModal.hide();
      EventBus.$emit('showLoader');
      await deleteSite(siteId);
      EventBus.$emit('triggerAlert', {
        type: 'primary',
        message: 'Site deactivated',
      });
      this.selectedSiteId = null;
      await this.loadSites();
      this.state = 'sites';
      EventBus.$emit('hideLoader');
    },
    async activate(siteId) {
      this.$refs.siteModal.hide();
      EventBus.$emit('showLoader');
      await activateSite(siteId);
      EventBus.$emit('triggerAlert', {
        type: 'primary',
        message: 'Site activated',
      });
      this.selectedSiteId = null;
      await this.loadSites();
      this.state = 'sites';
      EventBus.$emit('hideLoader');
    },
    async cancelEditing() {
      await this.$refs.siteModal.hide();
    },
    async saved() {
      EventBus.$emit('showLoader');
      this.state = 'sites';
      await this.$refs.siteModal.hide();
      await this.loadSites();
    },
    async backToSites() {
      this.state = 'sites';
    },
  },
};
</script>
<style>
.sites tr {
  cursor: pointer;
}

.row.section-title {
    margin-top: 1rem;
    margin-bottom: 0.7rem;
    border-bottom: 1px solid darkgrey;
}

h3.site-name {
  width: 100%;
  text-align: center;
}

.btn-confirmation {
  width: calc(100% - 2rem);
  margin: auto;
}
</style>
